import React from "react";
import { Card } from "../Card";
import type { ImportResult, ImportEntityCount } from "../../../types";

const ENTITY_LABELS: Record<string, string> = {
  categories: "Categorias mãe",
  subcategories: "Categorias",
  transactions: "Transações",
  bankAccounts: "Contas",
};

function formatEntityCount(name: string, count: ImportEntityCount): React.ReactNode[] {
  const label = ENTITY_LABELS[name] ?? name;
  const parts: React.ReactNode[] = [];
  parts.push(
    <span key={`${name}-created`}>
      {label} criadas: <strong>{count.created}</strong>
    </span>
  );
  if (count.skipped !== undefined && count.skipped > 0) {
    parts.push(
      <span key={`${name}-skipped`}>
        {label} ignoradas: <strong>{count.skipped}</strong>
      </span>
    );
  }
  if (count.failed !== undefined && count.failed > 0) {
    parts.push(
      <span key={`${name}-failed`}>
        {label} com falha: <strong>{count.failed}</strong>
      </span>
    );
  }
  return parts;
}

function SummaryLines({ result }: { result: ImportResult }) {
  const entities = result.entities ?? {};
  const keys = Object.keys(entities);
  if (keys.length === 0) return null;

  const allParts: React.ReactNode[] = [];
  keys.forEach((key, keyIndex) => {
    const count = entities[key];
    if (!count) return;
    const partNodes = formatEntityCount(key, count);
    partNodes.forEach((node, i) => {
      if (allParts.length > 0) allParts.push(" · ");
      allParts.push(<span key={`${key}-${i}`}>{node}</span>);
    });
  });

  return (
    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1 flex flex-wrap gap-x-1">
      {allParts}
    </p>
  );
}

interface ImportResultCardProps {
  result: ImportResult;
  onClose: () => void;
  title?: string;
  maxErrorsShown?: number;
}

export const ImportResultCard: React.FC<ImportResultCardProps> = ({
  result,
  onClose,
  title = "Importação concluída",
  maxErrorsShown = 100,
}) => {
  const errors = result.errors ?? [];
  const errorsToShow = errors.slice(0, maxErrorsShown);
  const hasMore = errors.length > maxErrorsShown;

  return (
    <Card className="p-6 border-l-4 border-amber-400 dark:border-amber-500 bg-amber-50/50 dark:bg-[#2D3748]">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <SummaryLines result={result} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white p-1 rounded"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        {errors.length > 0 && (
          <div className="rounded-lg border border-amber-200 dark:border-[#374151] bg-white dark:bg-[#374151] overflow-hidden">
            <div className="px-3 py-2 bg-amber-100/80 dark:bg-[#1A202C] text-sm font-medium text-gray-700 dark:text-slate-200">
              Erros (linha · mensagem)
            </div>
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-[#374151] text-sm">
              {errorsToShow.map((err, i) => (
                <li key={i} className="px-3 py-2 text-gray-700 dark:text-slate-300">
                  <span className="font-mono text-amber-800 dark:text-amber-400">{err.row}</span>
                  {" · "}
                  {err.message}
                </li>
              ))}
            </ul>
            {hasMore && (
              <div className="px-3 py-2 bg-gray-50 dark:bg-[#1A202C] text-gray-500 dark:text-slate-400 text-sm">
                Mostrando {maxErrorsShown} de {errors.length} erros
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
